/**
 * TaskDetailPage.tsx — Task Manager detail (Evo Brain Phase 1)
 * Route /tasks/:id. Projects-detail structure: merged SubNav row (back + priority/
 * status pills, no tabs — task content has no distinct workflow stages) over a
 * 1200px-maxWidth content column (DetailSection blocks: info via KVGrid/KVRow,
 * description, result/evidence, last error), with the Claude assistant panel
 * mounted as the right column, same shell/position as every other module. Title
 * and page-level actions (approve/retry/edit/delete) live in the TopBar override
 * (bottomActions), same slot Lead/Project detail use — per-task edit lives here
 * (not in bulk).
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Icon } from '../components/Icon.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { KVRow } from '../components/ui/KVRow.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { Textarea } from '../components/ui/Textarea.js';
import { Select } from '../components/ui/Select.js';
import { DatePicker } from '../components/ui/DatePicker.js';
import { Field } from '../components/ui/Field.js';
import { Pill, PillGroup, type PillTone } from '../components/PillGroup.js';
import { ApprovalCard, type ApprovalProposalItem, type ProposalVerb } from '../components/ui/ApprovalCard.js';
import { TaskClaudePanel } from '../components/tasks/TaskClaudePanel.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import {
  useTask, useApproveTask, useRetryTask, useRunTaskNow, useDeleteTask,
  usePatchTask, useTaskWorkspaceFiles,
  useApproveProposalTask, useRejectProposalTask,
  type TaskStatus, type Task,
} from '../hooks/useTasks.js';
import { useProject } from '../hooks/useProjects.js';
import { useContact } from '../hooks/useContacts.js';
import { useReportsForTask } from '../hooks/useReports.js';
import { parseServerDate } from '../lib/date.js';

// Corrección (bug real, hallado en validación visual): projects usa ruta por
// SLUG (/proyectos/:slug, ver App.tsx), no por id — un href con el id numérico
// no matcheaba ninguna ruta y redirigía a Home. Contacts no tiene ruta de
// detalle propia (split-pane en /contacts, sin :id) — el link va a la lista.
// Por eso se resuelve la entidad acá con un fetch liviano (React Query,
// cacheado) en vez de construir el href a mano desde entityType+entityId.
function useProposalEntity(
  entityType: string | null,
  entityId: string | null,
): { label: string; href: string } | null {
  const projectId = entityType === 'project' && entityId ? parseInt(entityId, 10) : undefined;
  const contactId = entityType === 'contact' && entityId ? parseInt(entityId, 10) : null;
  const { data: project } = useProject(entityType === 'project' ? projectId : undefined);
  const { data: contact } = useContact(entityType === 'contact' ? contactId : null);

  if (entityType === 'project') {
    if (!project) return null; // aún cargando — la card se muestra sin link hasta resolver
    return { label: project.name, href: `/proyectos/${project.slug}` };
  }
  if (entityType === 'contact') {
    // Nombre completo Y email — esta card es a veces la única confirmación
    // que Eduardo ve de un contacto resuelto por voz a partir de texto libre
    // (nunca estuvo en pantalla). Ver Evo Brain Parte 1 (find_contact).
    const name = contact
      ? ([contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || `#${entityId}`) +
        (contact.primaryEmail ? ` (${contact.primaryEmail})` : '')
      : `#${entityId ?? ''}`;
    return { label: name, href: '/contacts' }; // sin ruta de detalle propia — va a la lista
  }
  if (entityType === 'lead' && entityId) {
    return { label: `Lead #${entityId}`, href: `/leads/${entityId}` };
  }
  return null;
}

const STATUS_TONES: Record<TaskStatus, PillTone> = {
  pending: 'neutral', needs_approval: 'amber', approved: 'cyan', in_progress: 'blue',
  done: 'success', failed: 'danger', blocked: 'muted',
};
const PRIORITY_TONES: Record<string, PillTone> = { alta: 'danger', media: 'amber', baja: 'muted' };

// 'web-review' queda fuera a propósito — deprecado (ver evobrain/agents/_deprecated_web-review).
// Una task existente con ese tipo lo sigue mostrando (KVRow con defaultValue), pero no es
// elegible como valor nuevo desde el form de edición.
const TYPE_VALUES = ['general', 'research', 'write', 'disk-check', 'sysadmin'] as const;
const PRIORITY_VALUES = ['alta', 'media', 'baja'] as const;
const OUTPUT_MODE_VALUES = ['per_run', 'single_doc'] as const;

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Edit modal ────────────────────────────────────────────────────────────────

interface EditForm {
  title: string;
  description: string;
  type: string;
  priority: string;
  category: string;
  outputMode: string;
  nextRunAt: Date | null;
  repeatMinutes: string;
  repeatUntil: Date | null;
  maxRuns: string;
}

function taskToForm(task: Task): EditForm {
  return {
    title: task.title,
    description: task.description,
    type: task.type,
    priority: task.priority,
    category: task.category,
    outputMode: task.outputMode,
    nextRunAt: task.nextRunAt ? parseServerDate(task.nextRunAt) : null,
    repeatMinutes: task.repeatMinutes != null ? String(task.repeatMinutes) : '',
    repeatUntil: task.repeatUntil ? parseServerDate(task.repeatUntil) : null,
    maxRuns: task.maxRuns != null ? String(task.maxRuns) : '',
  };
}

function EditTaskModal({ task, open, onClose }: { task: Task; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const patch = usePatchTask();
  const [form, setForm] = useState<EditForm>(() => taskToForm(task));

  // Reabrir con datos frescos cada vez que se abre (no persistir edits a medio hacer entre aperturas).
  useEffect(() => { if (open) setForm(taskToForm(task)); }, [open, task]);

  function set<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function handleSave() {
    const repeatMinutes = form.repeatMinutes.trim() ? parseInt(form.repeatMinutes, 10) : null;
    const maxRuns = form.maxRuns.trim() ? parseInt(form.maxRuns, 10) : null;
    patch.mutate(
      {
        id: task.id,
        title: form.title.trim(),
        description: form.description,
        type: form.type,
        priority: form.priority,
        category: form.category,
        outputMode: form.outputMode,
        nextRunAt: form.nextRunAt ? form.nextRunAt.toISOString() : null,
        repeatMinutes: Number.isFinite(repeatMinutes) ? repeatMinutes : null,
        repeatUntil: form.repeatUntil ? form.repeatUntil.toISOString() : null,
        maxRuns: Number.isFinite(maxRuns) ? maxRuns : null,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={t('tasks.edit.title')} size="lg">
      <div className="flex flex-col" style={{ gap: '4px' }}>
        <Field label={t('tasks.fields.title')}>
          <Input value={form.title} onChange={v => set('title', v)} fullWidth />
        </Field>
        <Field label={t('tasks.fields.description')}>
          <Textarea value={form.description} onChange={v => set('description', v)} rows={8} fullWidth />
        </Field>
        <Field label={t('tasks.fields.type')}>
          <Select
            value={form.type}
            onChange={v => set('type', v ?? form.type)}
            options={TYPE_VALUES.map(v => ({ value: v, label: t(`tasks.type.${v}`, { defaultValue: v }) }))}
            fullWidth
          />
        </Field>
        <Field label={t('tasks.fields.priority')}>
          <Select
            value={form.priority}
            onChange={v => set('priority', v ?? form.priority)}
            options={PRIORITY_VALUES.map(v => ({ value: v, label: t(`tasks.priority.${v}`, { defaultValue: v }) }))}
            fullWidth
          />
        </Field>
        <Field label={t('tasks.fields.category')}>
          <Input value={form.category} onChange={v => set('category', v)} fullWidth />
        </Field>
        <Field label={t('tasks.fields.outputMode')}>
          <Select
            value={form.outputMode}
            onChange={v => set('outputMode', v ?? form.outputMode)}
            options={OUTPUT_MODE_VALUES.map(v => ({ value: v, label: t(`tasks.outputMode.${v}`, { defaultValue: v }) }))}
            fullWidth
          />
        </Field>
        <Field label={t('tasks.fields.nextRunAt')}>
          <DatePicker mode="single" value={form.nextRunAt} onChange={v => set('nextRunAt', v)} fullWidth />
        </Field>
        <Field label={t('tasks.fields.repeatMinutes')}>
          <Input type="number" value={form.repeatMinutes} onChange={v => set('repeatMinutes', v)} fullWidth />
        </Field>
        <Field label={t('tasks.fields.repeatUntil')}>
          <DatePicker mode="single" value={form.repeatUntil} onChange={v => set('repeatUntil', v)} fullWidth />
        </Field>
        <Field label={t('tasks.fields.maxRuns')}>
          <Input type="number" value={form.maxRuns} onChange={v => set('maxRuns', v)} fullWidth />
        </Field>
      </div>
      <div className="flex justify-end" style={{ gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-4)' }}>
        <Button variant="outline" tone="muted" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="solid" tone="cyan" size="sm" loading={patch.isPending} onClick={handleSave}>{t('common.save')}</Button>
      </div>
    </Modal>
  );
}

// ── Workspace files (lo que produjo la corrida — lectura directa del filesystem,
// nunca stale: no hay columna ni caché, se lista al pedirla) ─────────────────

function WorkspaceFilesSection({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useTaskWorkspaceFiles(taskId);
  const files = data?.files ?? [];

  if (isLoading) return null;

  return (
    <DetailSection title={t('tasks.detail.files')}>
      {files.length === 0 ? (
        <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', margin: 0 }}>
          {t('tasks.detail.filesEmpty')}
        </p>
      ) : (
        <div className="flex flex-col" style={{ gap: '4px' }}>
          {files.map(f => (
            <div key={f.name} className="flex items-center justify-between" style={{ padding: '4px 0', borderBottom: '1px solid var(--evo-stroke)' }}>
              <span style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'monospace', color: 'var(--evo-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', flexShrink: 0, marginLeft: 'var(--evo-space-3)' }}>
                {fmtBytes(f.sizeBytes)} · {fmt(f.modifiedAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </DetailSection>
  );
}

// ── Reports que esta task produjo (link real vía reports.task_id, nunca
// derivado por timestamp/título — ver reports/service.ts) ───────────────────

function TaskReportsSection({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useReportsForTask(taskId);
  const reports = data?.reports ?? [];

  if (reports.length === 0) return null;

  return (
    <DetailSection title={t('tasks.detail.reports')}>
      <div className="flex flex-col" style={{ gap: '4px' }}>
        {reports.map(r => (
          <div key={r.id} className="flex items-center justify-between" style={{ padding: '4px 0', borderBottom: '1px solid var(--evo-stroke)' }}>
            <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.title}
            </span>
            <Button size="xs" variant="outline" tone="cyan" onClick={() => navigate(`/reports/${r.slug}`)}>
              {t('tasks.detail.viewReport')}
            </Button>
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

export function TaskDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: task, isLoading } = useTask(id ?? null);
  const { setOverride } = useTopBarCtx();
  const approve = useApproveTask();
  const approveProposal = useApproveProposalTask();
  const rejectProposal = useRejectProposalTask();
  const retry = useRetryTask();
  const runNow = useRunTaskNow();
  const del = useDeleteTask();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Hook siempre se llama (Rules of Hooks) — antes del early return de loading.
  const proposalEntity = useProposalEntity(task?.entityType ?? null, task?.entityId ?? null);

  // Título + acciones de página en el TopBar override — mismo slot que Lead/Project
  // detail (bottomActions), en vez de un header propio con el título inline.
  useEffect(() => {
    setOverride({
      title: task?.title ?? t('tasks.pageTitle'),
      ...(task ? { subtitle: t(`tasks.type.${task.type}`, { defaultValue: task.type }) } : {}),
      heroIcon: 'bolt-large',
      heroIconColor: 'var(--evo-steel)',
      ...(task ? {
        bottomActions: (
          <PillGroup size="md">
            {!confirmDelete ? (
              <>
                {(task.status === 'needs_approval' || task.status === 'pending') && task.executor !== 'tool' && (
                  <Button size="sm" variant="outline" tone="cyan" loading={approve.isPending} onClick={() => approve.mutate(task.id)}>{t('tasks.actions.approve')}</Button>
                )}
                {task.status === 'approved' && task.executor !== 'tool' && (
                  <Button size="sm" variant="outline" tone="cyan" loading={runNow.isPending} onClick={() => runNow.mutate(task.id)}>{t('tasks.actions.runNow')}</Button>
                )}
                {(task.status === 'failed' || task.status === 'blocked' || task.status === 'done') && (
                  <Button size="sm" variant="outline" tone="cyan" loading={retry.isPending} onClick={() => retry.mutate(task.id)}>{t('tasks.actions.retry')}</Button>
                )}
                {task.status !== 'in_progress' && (
                  <Button size="sm" variant="outline" tone="muted" icon={<Icon name="pencil" size={14} />} onClick={() => setEditOpen(true)}>{t('tasks.actions.edit')}</Button>
                )}
                <Button size="sm" variant="outline" tone="danger" onClick={() => setConfirmDelete(true)}>{t('tasks.actions.delete')}</Button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap' }}>{t('tasks.deleteConfirm')}</span>
                <Button size="sm" variant="solid" tone="danger" loading={del.isPending} onClick={() => del.mutate(task.id, { onSuccess: () => navigate('/tasks') })}>{t('tasks.actions.delete')}</Button>
                <Button size="sm" variant="outline" tone="muted" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
              </>
            )}
          </PillGroup>
        ),
      } : {}),
    });
    return () => setOverride({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: .isPending booleans en vez de los objetos de mutation completos (refs inestables); navigate/setOverride/t son estables
  }, [task, confirmDelete, setOverride, t, approve.isPending, runNow.isPending, retry.isPending, del.isPending]);

  if (isLoading || !task) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LoadingState fullHeight />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* SubNav — back + priority/status pills. Sin tabs: a diferencia de Projects
          (7 etapas de workflow distintas), el contenido de una task (info/
          descripción/error/resultado/archivos) no tiene etapas comparables. */}
      <div style={{
        height: 'var(--evo-subnav-h)',
        borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0,
        background: 'var(--evo-bg)',
      }}>
        <div style={{ padding: '0 var(--evo-content-pad-x)', height: '100%' }}>
        <div style={{ maxWidth: '1200px', height: '100%', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            variant="ghost"
            tone="muted"
            size="sm"
            icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow-right" size={12} /></span>}
            onClick={() => navigate('/tasks')}
          >
            {t('tasks.detail.back')}
          </Button>
          <div style={{ width: '1px', height: '16px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
          <Pill tone={PRIORITY_TONES[task.priority] ?? 'muted'} variant="outline" size="sm">
            {t(`tasks.priority.${task.priority}`, { defaultValue: task.priority })}
          </Pill>
          <Pill tone={STATUS_TONES[task.status] ?? 'neutral'} variant="outline" size="sm">
            {t(`tasks.status.${task.status}`)}
          </Pill>
        </div>
        </div>
      </div>

      {/* Main row: left (contenido) + right (Claude panel) */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: scrollable content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
            <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>

              {/* Línea en castellano llano de qué va a pasar al ejecutar — siempre
                  visible, sin jerga. Si la task no la tiene (creador no la proveyó,
                  o importada), no se inventa acá: el bloque simplemente no se monta. */}
              {task.plainSummary && (
                <p style={{
                  fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-1)',
                  fontFamily: 'var(--evo-font)', lineHeight: 1.5, margin: '0 0 var(--evo-space-5)',
                }}>
                  {task.plainSummary}
                </p>
              )}

              {/* remitentes DM desconocidos: la task solo agrupa y avisa — decidir
                  pasa en /mensajes/triage (nombre, fragmento del mensaje, fecha). */}
              {task.category === 'dm_unknown_senders' && (
                <div style={{ marginBottom: 'var(--evo-space-5)' }}>
                  <Button variant="solid" tone="cyan" size="sm" onClick={() => navigate('/mensajes/triage')}>
                    {t('tasks.detail.goToTriage')}
                  </Button>
                </div>
              )}

              {/* pending, agent-cli: hace explícito que la task espera aprobación —
                  antes no había ninguna señal más allá del pill de estado, y el botón
                  de acción disponible (Aprobar, en el TopBar) podía pasar desapercibido. */}
              {task.status === 'pending' && task.executor === 'agent-cli' && (
                <div style={{
                  background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-sm)',
                  padding: '10px 12px', marginBottom: 'var(--evo-space-4)',
                }}>
                  <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
                    {t('tasks.detail.pendingHint')}
                  </span>
                </div>
              )}

              {/* Evo Brain: propuesta gated — approve/reject vive acá, no en KVGrid genérico */}
              {task.executor === 'tool' && task.proposedActionJson && (() => {
                type ProposedAction = { toolName: string; turnGroupId: string; verb: ProposalVerb; summary: { sentence: string; changes?: Array<{ field: string; from: string | null; to: string }>; irreversible?: boolean; warning?: string } };
                let parsed: ProposedAction | null = null;
                try {
                  parsed = JSON.parse(task.proposedActionJson) as ProposedAction;
                } catch { /* payload corrupto — se omite la card, el resto de la página sigue */ }
                if (!parsed) return null;
                const item: ApprovalProposalItem = {
                  id: task.proposalMessageId ?? 0,
                  taskId: task.id,
                  turnGroupId: parsed.turnGroupId,
                  toolName: parsed.toolName,
                  verb: parsed.verb,
                  sentence: parsed.summary.sentence,
                  changes: parsed.summary.changes,
                  irreversible: parsed.summary.irreversible,
                  warning: parsed.summary.warning,
                  createdAt: task.createdAt,
                  status: task.status === 'done' ? 'approved' : task.status === 'failed' ? 'rejected' : 'pending',
                };
                return (
                  <div style={{ marginBottom: 'var(--evo-space-5)' }}>
                    <ApprovalCard
                      proposals={[item]}
                      entity={proposalEntity}
                      onApprove={(id, remember) => approveProposal.mutate({ id, remember: Boolean(remember) })}
                      onReject={id => rejectProposal.mutate(id)}
                      approvingId={approveProposal.isPending ? task.id : null}
                      rejectingId={rejectProposal.isPending ? task.id : null}
                    />
                  </div>
                );
              })()}

              <DetailSection title={t('tasks.detail.info')}>
                <KVGrid columns={2}>
                  <KVRow label={t('tasks.fields.type')} value={t(`tasks.type.${task.type}`, { defaultValue: task.type })} />
                  <KVRow label={t('tasks.fields.outputMode')} value={t(`tasks.outputMode.${task.outputMode}`, { defaultValue: task.outputMode })} />
                  <KVRow label={t('tasks.fields.nextRunAt')} value={fmt(task.nextRunAt)} />
                  <KVRow label={t('tasks.fields.repeatMinutes')} value={task.repeatMinutes ? `${task.repeatMinutes} min` : '—'} />
                  <KVRow label={t('tasks.fields.attempts')} value={`${task.attempts}`} />
                  <KVRow label={t('tasks.fields.runs')} value={`${task.runs}`} last />
                </KVGrid>
              </DetailSection>

              {task.description && (
                <DetailSection
                  title={t('tasks.fields.description')}
                  titleRight={
                    <SectionHeaderAction onClick={() => setDescExpanded(v => !v)}>
                      {descExpanded ? t('tasks.detail.collapseDescription') : '+'}
                    </SectionHeaderAction>
                  }
                >
                  {descExpanded && (
                    <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap', margin: 0 }}>{task.description}</p>
                  )}
                </DetailSection>
              )}

              {task.lastError && (
                <DetailSection title={t('tasks.detail.lastError')}>
                  <pre style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--evo-danger)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{task.lastError}</pre>
                </DetailSection>
              )}

              {task.result && (
                <DetailSection title={t('tasks.detail.result')}>
                  <pre style={{ fontSize: '12px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{task.result}</pre>
                </DetailSection>
              )}

              {task.executor === 'agent-cli' && <TaskReportsSection taskId={task.id} />}

              {/* Workspace: solo tiene sentido para agent-cli (executor='tool' nunca
                  genera archivos — ejecuta in-server) y solo una vez que corrió al
                  menos una vez. OJO: attempts NO sirve para esto — completeTask()
                  lo resetea a 0 en cada re-arme exitoso de una task recurrente
                  (cuenta fallos consecutivos del ciclo actual, no corridas totales).
                  runs sí persiste y solo crece — es el campo correcto acá. */}
              {task.executor === 'agent-cli' && task.runs > 0 && (
                <WorkspaceFilesSection taskId={task.id} />
              )}

            </div>
            </div>
          </div>
        </div>

        {/* Right: Claude panel — scoped a esta task, mismo shell/posición que
            Project/Contact/Lead/Report/Billing/EmailClaudePanel. */}
        <TaskClaudePanel task={task} />

      </div>

      {task.status !== 'in_progress' && (
        <EditTaskModal task={task} open={editOpen} onClose={() => setEditOpen(false)} />
      )}
    </div>
  );
}
